import React, { useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { useT } from "../../lib/i18n";
import { Empty } from "./OverviewTab";

const BAND_CLASS = {
  "Acceptable": "cell-acceptable",
  "ALARP": "cell-alarp",
  "Undesirable": "cell-undesirable",
  "Unacceptable": "cell-unacceptable",
};
const SEVERITY_LEVELS = ["Negligible", "Minor", "Serious", "Critical", "Catastrophic"];
const PROBABILITY_LEVELS = ["Improbable", "Remote", "Occasional", "Probable", "Frequent"];
const EMPTY_FORM = {
  category: "",
  hazard: "",
  hazardous_situation: "",
  harm: "",
  initial_severity: "",
  initial_probability: "",
  controls: "",
  residual_severity: "",
  residual_probability: "",
};

export default function RiskTab({ project, version, deviceId, onChange, onOpenAiAct }) {
  const t = useT();
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);

  if (!version) return <Empty msg="No analysis yet." />;
  const r = version.risk || {};
  const aiAct = version.ai_act || {};
  const legacyHighRisk = aiAct.ai_classification === "high_risk";
  const prioritizeAiActReview = aiAct.potential_high_risk_review === true || legacyHighRisk;
  const matrix = {
    severity: r.matrix?.severity?.length ? r.matrix.severity : SEVERITY_LEVELS,
    probability: r.matrix?.probability?.length ? r.matrix.probability : PROBABILITY_LEVELS,
  };
  const rows = normalizeRows(r);
  const summary = r.summary || summarizeRows(rows);
  const canAddRisk = Boolean(project?.id && version.version && !version.frozen_at);

  function setField(field, value) {
    setForm((current) => ({ ...current, [field]: value }));
    setError("");
    setSaved(false);
  }

  async function saveRisk(event) {
    event.preventDefault();
    setSaving(true);
    setError("");
    setSaved(false);
    try {
      const payload = {
        ...form,
        controls: form.controls.split("\n").map((control) => control.trim()).filter(Boolean),
      };
      await api.post(
        `/projects/${project.id}/versions/${version.version}/risks`,
        payload,
        { params: deviceId ? { device_id: deviceId } : {} },
      );
      setForm(EMPTY_FORM);
      setShowForm(false);
      setSaved(true);
      await onChange?.();
    } catch (saveError) {
      setError(formatApiError(saveError));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-4" data-testid="risk-management">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Stat label={t("risk.total")} value={summary.total_hazards ?? rows.length} />
        <Stat label={t("risk.initial_unacceptable")} value={summary.unacceptable_initial ?? 0} red={summary.unacceptable_initial > 0} />
        <Stat label={t("risk.residual_unacceptable")} value={summary.unacceptable_residual ?? 0} red={summary.unacceptable_residual > 0} />
      </div>

      {aiAct.applicable && (
        <section className="grid-card space-y-3 border-l-4 border-l-amber-500 p-4" data-testid="risk-ai-act-context">
          <h2 className="font-semibold">{t("risk.ai_act_title")}</h2>
          {prioritizeAiActReview && (
            <p className="text-sm font-semibold text-amber-700 dark:text-amber-300" data-testid="risk-ai-act-priority">
              {legacyHighRisk ? t("aiact.legacy_high_risk") : t("risk.ai_act_priority")}
            </p>
          )}
          <p className="text-sm leading-relaxed text-[var(--text-2)]">{t("risk.ai_act_note")}</p>
          {onOpenAiAct && (
            <button type="button" onClick={onOpenAiAct} className="w-fit font-semibold text-[var(--accent-text)] hover:underline" data-testid="risk-open-ai-act">
              {t("risk.ai_act_open")} →
            </button>
          )}
        </section>
      )}

      <div className="grid-card p-5">
        <div className="text-xs font-mono uppercase tracking-wider text-[var(--text-2)] mb-3">{t("risk.matrix")}</div>
        <div className="overflow-auto">
          <table className="border-collapse text-xs font-mono text-[var(--text)]">
            <thead>
              <tr>
                <th className="risk-table-head border px-3 py-2">{t("risk.severity_short")} \ {t("risk.probability_short")}</th>
                {matrix.probability.map((probability) => (
                  <th key={probability} className="risk-table-head border px-3 py-2">{levelLabel(probability, t)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {matrix.severity.slice().reverse().map((severity) => (
                <tr key={severity}>
                  <td className="risk-table-head border px-3 py-2 font-bold">{levelLabel(severity, t)}</td>
                  {matrix.probability.map((probability) => {
                    const score = (matrix.severity.indexOf(severity) + 1) * (matrix.probability.indexOf(probability) + 1);
                    const band = riskBand(score);
                    return <td key={probability} className={`border risk-table-cell px-4 py-3 text-center ${BAND_CLASS[band]}`} data-testid={`matrix-${severity}-${probability}`}>{score}</td>;
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="grid-card p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="font-semibold">{t("risk.register_title")}</h2>
          <p className="text-sm text-[var(--text-2)] mt-1">{t("risk.generated_note")}</p>
        </div>
        <button
          type="button"
          onClick={() => { setShowForm((open) => !open); setError(""); setSaved(false); }}
          disabled={!canAddRisk}
          className="btn-primary px-4 py-2 text-sm disabled:opacity-50"
          data-testid="risk-add-toggle"
        >
          {showForm ? t("risk.cancel") : t("risk.add")}
        </button>
      </div>

      {version.frozen_at && (
        <div className="grid-card p-3 text-sm text-[var(--risk-warning)]" role="status" data-testid="risk-frozen-note">
          {t("risk.frozen")}
        </div>
      )}
      {saved && <div className="text-sm text-[var(--risk-success)]" role="status">{t("risk.saved")}</div>}
      {error && <div className="grid-card p-3 text-sm text-[var(--risk-danger)]" role="alert" data-testid="risk-save-error">{error}</div>}

      {showForm && canAddRisk && (
        <form onSubmit={saveRisk} className="grid-card p-4 sm:p-5 space-y-4" data-testid="risk-entry-form">
          <h3 className="font-semibold">{t("risk.new_entry")}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <TextField label={t("risk.category")} value={form.category} onChange={(value) => setField("category", value)} testid="risk-category" required />
            <TextField label={t("risk.description")} value={form.hazard} onChange={(value) => setField("hazard", value)} testid="risk-description" required multiline />
            <TextField label={t("risk.situation")} value={form.hazardous_situation} onChange={(value) => setField("hazardous_situation", value)} testid="risk-situation" multiline />
            <TextField label={t("risk.harm")} value={form.harm} onChange={(value) => setField("harm", value)} testid="risk-harm" multiline />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <RiskRatingFields
              title={t("risk.initial")}
              severity={form.initial_severity}
              probability={form.initial_probability}
              onSeverity={(value) => setField("initial_severity", value)}
              onProbability={(value) => setField("initial_probability", value)}
              t={t}
              testPrefix="risk-initial"
            />
            <RiskRatingFields
              title={t("risk.residual")}
              severity={form.residual_severity}
              probability={form.residual_probability}
              onSeverity={(value) => setField("residual_severity", value)}
              onProbability={(value) => setField("residual_probability", value)}
              t={t}
              testPrefix="risk-residual"
            />
          </div>

          <TextField label={t("risk.controls")} value={form.controls} onChange={(value) => setField("controls", value)} testid="risk-controls" multiline hint={t("risk.controls_hint")} />
          <div className="flex justify-end">
            <button type="submit" className="btn-primary px-4 py-2 text-sm" disabled={saving} data-testid="risk-save">
              {saving ? t("risk.saving") : t("risk.save")}
            </button>
          </div>
        </form>
      )}

      <div className="grid-card p-0 overflow-hidden" data-testid="risk-table">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px] text-sm text-[var(--text)]">
            <thead className="risk-table-head border-b">
              <tr className="text-left">
                <th className="px-4 py-3 font-mono text-xs uppercase">{t("risk.id")}</th>
                <th className="px-4 py-3 font-mono text-xs uppercase">{t("risk.category")}</th>
                <th className="px-4 py-3 font-mono text-xs uppercase">{t("risk.description")}</th>
                <th className="px-4 py-3 font-mono text-xs uppercase">{t("risk.initial")}</th>
                <th className="px-4 py-3 font-mono text-xs uppercase">{t("risk.controls")}</th>
                <th className="px-4 py-3 font-mono text-xs uppercase">{t("risk.residual")}</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan="6" className="px-4 py-8 text-center text-[var(--text-2)]">{t("risk.empty")}</td></tr>
              ) : rows.map((row, index) => (
                <tr key={row.hazard_id} className={`risk-table-row border-b ${index % 2 === 1 ? "risk-table-row-alt" : ""}`} data-testid={`risk-row-${row.hazard_id}`}>
                  <td className="px-4 py-3 align-top font-mono text-xs">
                    {row.hazard_id}
                    {row.source === "manual" && <span className="block mt-1 text-[10px] text-[var(--text-2)]">{t("risk.manual")}</span>}
                  </td>
                  <td className="px-4 py-3 align-top">{row.category}</td>
                  <td className="px-4 py-3 align-top whitespace-normal">
                    <p className="font-medium">{row.hazard}</p>
                    {row.hazardous_situation && <p className="mt-2 text-xs text-[var(--text-2)]"><strong>{t("risk.situation")}:</strong> {row.hazardous_situation}</p>}
                    {row.harm && <p className="mt-1 text-xs text-[var(--text-2)]"><strong>{t("risk.harm")}:</strong> {row.harm}</p>}
                  </td>
                  <td className={`px-4 py-3 align-top font-mono text-xs ${BAND_CLASS[row.initial_risk.band] || ""}`}>
                    {formatRisk(row.initial_risk, t)}
                  </td>
                  <td className="px-4 py-3 align-top text-xs">
                    {row.controls.length ? <ul className="list-disc list-inside space-y-1">{row.controls.map((control, controlIndex) => <li key={`${row.hazard_id}-${controlIndex}`}>{control}</li>)}</ul> : "—"}
                  </td>
                  <td className={`px-4 py-3 align-top font-mono text-xs ${BAND_CLASS[row.residual_risk.band] || ""}`}>
                    {formatRisk(row.residual_risk, t)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function normalizeRows(risk) {
  const sourceRows = Array.isArray(risk)
    ? risk
    : (risk.rows?.length ? risk.rows : (risk.hazards || risk.rows || []));
  if (!Array.isArray(sourceRows)) return [];
  return sourceRows.map((row, index) => ({
    ...row,
    hazard_id: row.hazard_id || row.id || `R-${index + 1}`,
    category: row.category || row.type || "—",
    hazard: row.hazard || row.description || row.risk || "—",
    hazardous_situation: row.hazardous_situation || row.situation || "",
    harm: row.harm || "",
    initial_risk: row.initial_risk || { band: row.initial_band || "—", score: row.initial_score ?? "—" },
    residual_risk: row.residual_risk || { band: row.residual_band || "—", score: row.residual_score ?? "—" },
    controls: Array.isArray(row.controls) ? row.controls : (row.controls ? [row.controls] : []),
  }));
}

function summarizeRows(rows) {
  return {
    total_hazards: rows.length,
    unacceptable_initial: rows.filter((row) => row.initial_risk.band === "Unacceptable").length,
    unacceptable_residual: rows.filter((row) => row.residual_risk.band === "Unacceptable").length,
  };
}

function riskBand(score) {
  return score <= 4 ? "Acceptable" : score <= 9 ? "ALARP" : score <= 15 ? "Undesirable" : "Unacceptable";
}

function formatRisk(value, t) {
  const rating = value?.band || "—";
  const label = rating === "—" ? rating : t(`risk.band.${rating.toLowerCase()}`);
  return value?.score !== undefined && value.score !== "—" ? `${label} (${value.score})` : label;
}

function levelLabel(level, t) {
  const key = {
    Negligible: "risk.level.negligible",
    Minor: "risk.level.minor",
    Serious: "risk.level.serious",
    Critical: "risk.level.critical",
    Catastrophic: "risk.level.catastrophic",
    Improbable: "risk.level.improbable",
    Remote: "risk.level.remote",
    Occasional: "risk.level.occasional",
    Probable: "risk.level.probable",
    Frequent: "risk.level.frequent",
  }[level];
  return key ? t(key) : level;
}

function TextField({ label, value, onChange, testid, required, multiline, hint }) {
  const shared = "mt-1 w-full rounded-sm border border-[var(--border)] bg-[var(--surface-elevated)] px-3 py-2 text-sm text-[var(--text)] focus:border-cyan-500 focus:outline-none";
  return (
    <label className="block text-xs font-semibold text-[var(--text-2)]">
      {label}{required ? " *" : ""}
      {multiline ? (
        <textarea className={`${shared} min-h-20 resize-y`} value={value} onChange={(event) => onChange(event.target.value)} required={required} data-testid={testid} />
      ) : (
        <input className={shared} value={value} onChange={(event) => onChange(event.target.value)} required={required} data-testid={testid} />
      )}
      {hint && <span className="mt-1 block font-normal">{hint}</span>}
    </label>
  );
}

function RiskRatingFields({ title, severity, probability, onSeverity, onProbability, t, testPrefix }) {
  const selectClass = "mt-1 w-full rounded-sm border border-[var(--border)] bg-[var(--surface-elevated)] px-3 py-2 text-sm text-[var(--text)]";
  return (
    <fieldset className="rounded-sm border border-[var(--border)] p-3">
      <legend className="px-1 text-xs font-semibold text-[var(--text-2)]">{title}</legend>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <label className="text-xs font-semibold text-[var(--text-2)]">
          {t("risk.severity")} *
          <select className={selectClass} value={severity} onChange={(event) => onSeverity(event.target.value)} required data-testid={`${testPrefix}-severity`}>
            <option value="">{t("risk.choose")}</option>
            {SEVERITY_LEVELS.map((level) => <option key={level} value={level}>{levelLabel(level, t)}</option>)}
          </select>
        </label>
        <label className="text-xs font-semibold text-[var(--text-2)]">
          {t("risk.probability")} *
          <select className={selectClass} value={probability} onChange={(event) => onProbability(event.target.value)} required data-testid={`${testPrefix}-probability`}>
            <option value="">{t("risk.choose")}</option>
            {PROBABILITY_LEVELS.map((level) => <option key={level} value={level}>{levelLabel(level, t)}</option>)}
          </select>
        </label>
      </div>
    </fieldset>
  );
}

function Stat({ label, value, red }) {
  return (
    <div className="grid-card p-4 sm:p-5">
      <div className="text-xs font-mono uppercase tracking-wider text-[var(--text-2)]">{label}</div>
      <div className={`font-mono text-3xl font-bold mt-2 ${red ? "text-[var(--risk-danger)]" : "text-[var(--risk-success)]"}`}>{value}</div>
    </div>
  );
}
